import { useEffect } from "react";

// MUI
import { Stack } from "@mui/material";

// Thrid party
import { useQueryClient } from "@tanstack/react-query";

// API
import { UPDATE_PAYROLL_PROVIDERS_CONFIGURED_ACCOUNTS } from "@/Settings/api/AdministrationTools";

// Hooks
import { useLocationId } from "@/Common/hooks/Location/useLocationId";

// Context
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// Components
import ProviderAuthenticatorGroup from "./components/ProviderAuthenticatorGroup";

// Types
import { GraphqlSubscriptionResponse } from "@/Common/api/types/GraphqlSubscriptionResponse";
import { IAdministrationTools, IUpdatedAdministrationToolsPayrollProvidersResponse } from "@Settings/types/administrationTools/model";

type AssetManagementSystemProps = {
	isExpanded: boolean;
	administrationTools: IAdministrationTools;
};

export default function PayrollProvider(props: Readonly<AssetManagementSystemProps>) {
	const { isExpanded, administrationTools } = props;
	const locationId = useLocationId();

	const { addSubscription, deleteSubscription, getSubscription } = useSubscriptions();
	const queryClient = useQueryClient();

	const payrollProviderSubscriptions = getSubscription(["onUpdateAdministrationToolsPayrollProviders", locationId]);
	payrollProviderSubscriptions?.subscriptions?.onUpdateAdministrationToolsPayrollProviders?.setCallback(
		(
			response: GraphqlSubscriptionResponse<
				"onUpdateAdministrationToolsPayrollProviders",
				IUpdatedAdministrationToolsPayrollProvidersResponse
			>
		) => {
			queryClient.setQueryData(["administrationTools", locationId], (cachedData: IAdministrationTools) => {
				if (!cachedData || !response?.data) {
					return cachedData;
				}
				const updatedPayrollProviders = response.data?.onUpdateAdministrationToolsPayrollProviders.payrollProviders;
				const locationIdToUpdate = response.data?.onUpdateAdministrationToolsPayrollProviders.locationId;

				if (cachedData.locationId !== locationIdToUpdate) {
					return cachedData;
				}

				return {
					...cachedData,
					integrations: {
						...cachedData.integrations,
						payrollProviders: updatedPayrollProviders,
					},
				};
			});
		}
	);

	useEffect(() => {
		addSubscription(
			["onUpdateAdministrationToolsPayrollProviders", locationId],
			[
				{
					context: "onUpdateAdministrationToolsPayrollProviders",
					query: UPDATE_PAYROLL_PROVIDERS_CONFIGURED_ACCOUNTS,
					variables: {
						locationId,
					},
				},
			]
		);
		return () => {
			deleteSubscription(["onUpdateAdministrationToolsPayrollProviders", locationId]);
		};
	}, [administrationTools?.integrations?.payrollProviders]);

	return (
		<Stack direction={"row"} marginTop={"5px"}>
			<ProviderAuthenticatorGroup
				locationId={locationId}
				payrollProviders={administrationTools?.integrations?.payrollProviders}
				isExpanded={isExpanded}
			/>
		</Stack>
	);
}
